import React, { useState, useEffect } from 'react';
import { Modal, Select } from 'antd';
import { FileSearchOutlined } from '@ant-design/icons';
import styled, { keyframes } from 'styled-components';
import dayjs from 'dayjs';
import { CancelButton, CreateButton } from '../../buttons';

// Types
export type AssessmentStatus = 'draft' | 'scheduled' | 'active' | 'completed';
export type AssessmentType = 'quiz' | 'exam' | 'test';

export interface AssessmentFormData {
  name: string;
  status: AssessmentStatus;
  type: AssessmentType;
  date: string;
  duration: number | null;
  description: string;
  classIds: number[];
  omrSheetId: number | null;
  gradingScaleId: number | null;
}

interface ClassOption {
  id: number;
  name: string;
}

interface AnswerSheetOption {
  id: number;
  name: string;
}

interface GradingScaleOption {
  id: number;
  name: string;
}

interface CreateAssessmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AssessmentFormData) => void;
  isLoading?: boolean;
  classes: ClassOption[];
  answerSheets: AnswerSheetOption[];
  gradingScales: GradingScaleOption[];
}

// Animations
const pulseAnimation = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

// Styled Components
const ModalContent = styled.div`
  padding: 20px 20px 16px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const IconContainer = styled.div`
  margin-bottom: 12px;
`;

const CreateIcon = styled.div`
  width: 48px;
  height: 48px;
  margin: 0 auto;
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 20px;
  animation: ${pulseAnimation} 2s ease-in-out infinite;
  box-shadow: 0 6px 16px rgba(22, 119, 255, 0.25);
`;

const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 4px 0;
  line-height: 1.3;
`;

const Subtitle = styled.p`
  font-size: 13px;
  color: #666;
  margin: 0;
  line-height: 1.4;
`;

const FormSection = styled.div`
  margin-bottom: 20px;
`;

const FormRow = styled.div`
  margin-bottom: 16px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 13px;
  font-weight: 500;
  color: #1a1a1a;
  margin-bottom: 8px;
`;

const StyledInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  
  .ant-select-selector {
    min-height: 40px !important;
    border-radius: 8px !important;
    border: 1px solid rgba(0, 0, 0, 0.15) !important;
    transition: all 0.2s ease;
    padding: 4px 8px !important;
    
    .ant-select-selection-item {
      line-height: 32px !important;
    }
    
    .ant-select-selection-placeholder {
      line-height: 32px !important;
    }
  }
  
  /* Styles for multiple select tags */
  &.ant-select-multiple .ant-select-selection-item {
    height: 28px !important;
    line-height: 26px !important;
    background: rgba(22, 119, 255, 0.1) !important;
    border: 1px solid rgba(22, 119, 255, 0.2) !important;
    border-radius: 6px !important;
    margin: 2px !important;
  }
  
  &:hover .ant-select-selector {
    border-color: #1677ff !important;
  }
  
  &.ant-select-focused .ant-select-selector {
    border-color: #1677ff !important;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1) !important;
  }
`;

const StyledDateInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
`;

const StyledNumberInput = styled.input`
  width: 100%;
  height: 40px;
  padding: 0 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
  
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  min-height: 80px;
  padding: 10px 12px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  font-size: 14px;
  color: #1a1a1a;
  transition: all 0.2s ease;
  box-sizing: border-box;
  resize: vertical;
  font-family: inherit;
  
  &:hover {
    border-color: #1677ff;
  }
  
  &:focus {
    outline: none;
    border-color: #1677ff;
    box-shadow: 0 0 0 2px rgba(22, 119, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(0, 0, 0, 0.45);
  }
`;

const SegmentGroup = styled.div`
  display: flex;
  border: 1px solid rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  width: 100%;
`;

const SegmentButton = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 40px;
  padding: 0 8px;
  border: none;
  background: ${props => props.$active ? 'linear-gradient(135deg, rgba(22, 119, 255, 0.15) 0%, rgba(18, 216, 250, 0.15) 100%)' : 'transparent'};
  color: ${props => props.$active ? '#1677ff' : '#666'};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all 0.2s ease;
  border-right: 1px solid rgba(0, 0, 0, 0.15);
  
  &:last-child {
    border-right: none;
  }
  
  &:hover {
    background: ${props => props.$active 
      ? 'linear-gradient(135deg, rgba(22, 119, 255, 0.15) 0%, rgba(18, 216, 250, 0.15) 100%)' 
      : 'rgba(0, 0, 0, 0.04)'};
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const OptionalLabel = styled.span`
  font-size: 11px;
  color: #999;
  font-weight: 400;
  margin-left: 4px;
`;

const ButtonSection = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
  margin-top: 24px;
`;

const SubmitButton = styled(CreateButton)`
  background: linear-gradient(135deg, #1FA2FF 0%, #12D8FA 100%) !important;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #0d8bf2 0%, #1FA2FF 100%) !important;
  }
`;

export const CreateAssessmentModal: React.FC<CreateAssessmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading = false,
  classes,
  answerSheets,
  gradingScales
}) => {
  const [formData, setFormData] = useState<AssessmentFormData>({
    name: '',
    status: 'draft',
    type: 'quiz',
    date: '',
    duration: null,
    description: '',
    classIds: [],
    omrSheetId: null,
    gradingScaleId: null
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        name: '',
        status: 'draft',
        type: 'quiz',
        date: '',
        duration: null,
        description: '',
        classIds: [],
        omrSheetId: null,
        gradingScaleId: null
      });
    }
  }, [isOpen]);
  
  const isFormValid = 
    formData.name.trim().length > 0 && 
    formData.status && 
    formData.type && 
    formData.classIds.length > 0 && 
    formData.omrSheetId !== null && 
    formData.gradingScaleId !== null &&
    // Date is required only when status is 'scheduled'
    (formData.status === 'draft' || (formData.status === 'scheduled' && formData.date));

  const handleSubmit = () => {
    if (isFormValid) {
      onSubmit(formData);
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      centered
      width={480}
      closable={false}
      maskClosable={!isLoading}
    >
      <ModalContent>
        <Header>
          <IconContainer>
            <CreateIcon>
              <FileSearchOutlined />
            </CreateIcon>
          </IconContainer>
          <Title>Create Assessment</Title>
          <Subtitle>Set up a new assessment for your class</Subtitle>
        </Header>

        <FormSection>
          {/* Assessment Name */}
          <FormRow>
            <Label>Assessment Name *</Label>
            <StyledInput
              type="text"
              placeholder="e.g. Math Quiz Week 1"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              disabled={isLoading}
            />
          </FormRow>

          {/* Assessment Type */}
          <FormRow>
            <Label>Assessment Type *</Label>
            <SegmentGroup>
              {(['quiz', 'exam', 'test'] as AssessmentType[]).map(type => (
                <SegmentButton
                  key={type}
                  $active={formData.type === type}
                  onClick={() => setFormData(prev => ({ ...prev, type }))}
                  type="button"
                  disabled={isLoading}
                >
                  {type.charAt(0).toUpperCase() + type.slice(1)}
                </SegmentButton>
              ))}
            </SegmentGroup>
          </FormRow>

          {/* Assessment Status */}
          <FormRow>
            <Label>Status *</Label>
            <SegmentGroup>
              {(['draft', 'scheduled'] as AssessmentStatus[]).map(status => (
                <SegmentButton
                  key={status}
                  $active={formData.status === status}
                  onClick={() => setFormData(prev => ({ 
                    ...prev, 
                    status,
                    // Reset date and duration when switching to draft
                    ...(status === 'draft' ? { date: '', duration: null } : {})
                  }))}
                  type="button"
                  disabled={isLoading}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </SegmentButton>
              ))}
            </SegmentGroup>
          </FormRow>

          {/* Date - only active when Scheduled */}
          <FormRow>
            <Label style={{ opacity: formData.status === 'scheduled' ? 1 : 0.5 }}>
              Date {formData.status === 'scheduled' && '*'}
            </Label>
            <StyledDateInput
              type="date"
              value={formData.date}
              onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))}
              disabled={isLoading || formData.status !== 'scheduled'}
              style={{ opacity: formData.status === 'scheduled' ? 1 : 0.5 }}
            />
          </FormRow>

          {/* Duration - only active when Scheduled */}
          <FormRow>
            <Label style={{ opacity: formData.status === 'scheduled' ? 1 : 0.5 }}>
              Duration <OptionalLabel>(minutes)</OptionalLabel>
            </Label>
            <StyledNumberInput
              type="number"
              placeholder="e.g. 60"
              min={1}
              max={600}
              value={formData.duration || ''}
              onChange={(e) => setFormData(prev => ({ 
                ...prev, 
                duration: e.target.value ? parseInt(e.target.value) : null 
              }))}
              disabled={isLoading || formData.status !== 'scheduled'}
              style={{ opacity: formData.status === 'scheduled' ? 1 : 0.5 }}
            />
          </FormRow>

          {/* Class Select */}
          <FormRow>
            <Label>Class *</Label>
            <StyledSelect
              mode="multiple"
              placeholder="Select classes"
              value={formData.classIds}
              onChange={(value) => setFormData(prev => ({ ...prev, classIds: value as number[] }))}
              disabled={isLoading}
              options={classes.map(c => ({ value: c.id, label: c.name }))}
            />
          </FormRow>

          {/* OMR Sheet Select */}
          <FormRow>
            <Label>OMR Sheet *</Label>
            <StyledSelect
              placeholder="Select OMR sheet"
              value={formData.omrSheetId}
              onChange={(value) => setFormData(prev => ({ ...prev, omrSheetId: value as number }))}
              disabled={isLoading}
              options={answerSheets.map(a => ({ value: a.id, label: a.name }))}
            />
          </FormRow>

          {/* Grading Scale Select */}
          <FormRow>
            <Label>Grading Scale *</Label>
            <StyledSelect
              placeholder="Select grading scale"
              value={formData.gradingScaleId}
              onChange={(value) => setFormData(prev => ({ ...prev, gradingScaleId: value as number }))}
              disabled={isLoading}
              options={gradingScales.map(g => ({ value: g.id, label: g.name }))}
            />
          </FormRow>

          {/* Description - at the bottom */}
          <FormRow>
            <Label>Description <OptionalLabel>(optional)</OptionalLabel></Label>
            <StyledTextarea
              placeholder="Enter assessment description..."
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              disabled={isLoading}
              rows={3}
            />
          </FormRow>
        </FormSection>

        <ButtonSection>
          <CancelButton 
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </CancelButton>
          
          <SubmitButton 
            onClick={handleSubmit}
            disabled={isLoading || !isFormValid}
            loading={isLoading}
          >
            {isLoading ? 'Creating...' : 'Create Assessment'}
          </SubmitButton>
        </ButtonSection>
      </ModalContent>
    </Modal>
  );
};

export default CreateAssessmentModal;

